import { defaultOptions, type UploadOptions } from "tus-js-client";
import type { FileUpload } from "./FileUpload";
import { throwError } from "../util/assertions/throwError";
import type { ILocalStorage } from "../util/io/LocalStorage";

export interface IDicomUploadTusFingerprintStore {
    clearAllFingerprintsFromLocalStorage(): void;
    clearFingerprintsFromLocalStorage(fileUploads: FileUpload[]): void;
}

export class DicomUploadTusFingerprintStore implements IDicomUploadTusFingerprintStore {
    public static readonly DicomUploadFingerprintPrefix = "dicom-upload-";

    constructor(private readonly localStorage: ILocalStorage) {}

    public static async generate(file: File, options: UploadOptions): Promise<string> {
        const defaultFingerprint = await defaultOptions.fingerprint(file, options);

        return DicomUploadTusFingerprintStore.DicomUploadFingerprintPrefix + defaultFingerprint;
    }

    public clearAllFingerprintsFromLocalStorage(): void {
        const { localStorage } = this;

        const localStorageKeysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (!key) {
                continue;
            }

            if (!key.startsWith("tus::" + DicomUploadTusFingerprintStore.DicomUploadFingerprintPrefix)) {
                continue;
            }

            localStorageKeysToRemove.push(key);
        }

        for (const key of localStorageKeysToRemove) {
            localStorage.removeItem(key);
        }
    }

    public clearFingerprintsFromLocalStorage(fileUploads: FileUpload[]): void {
        const { localStorage } = this;

        const localStorageKeysToRemove: string[] = [];

        for (const fileUpload of fileUploads) {
            const key = fileUpload.keyInLocalStorage ?? throwError("Tried to remove key from local storage, but key is null");
            const keyWithTusPrefix = `tus::${key}`;

            for (let i = 0; i < localStorage.length; i++) {
                const nthKey = localStorage.key(i);
                if (nthKey?.startsWith(keyWithTusPrefix)) {
                    localStorageKeysToRemove.push(nthKey);
                }
            }

            fileUpload.keyInLocalStorage = null;
        }

        for (const keyToRemove of localStorageKeysToRemove) {
            localStorage.removeItem(keyToRemove);
        }
    }
}
